import { LazoView, useLazoView } from "@/contexts/CurrentLazoViewContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { useResponsive } from "@/hooks/useResponsive";
import { Box, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

// Icons
import TaskIcon from "@mui/icons-material/AssignmentTurnedIn";
import MailIcon from "@mui/icons-material/Mail";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import NotificationsIcon from "@mui/icons-material/Notifications";

const BottomNavigationContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  height: "3rem",
  backgroundColor: Colors.BLACK,
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  zIndex: theme.zIndex.appBar,
  backdropFilter: "blur(10px)",
  // Hide on desktop - only show on mobile
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const NavButton = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  maxWidth: "80px",
  cursor: "pointer",
  borderRadius: "12px",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: `${Colors.DARK_GREY_3E3E3E}40`,
  },
  "&:active": {
    backgroundColor: `${Colors.DARK_GREY_3E3E3E}60`,
    transform: "scale(0.95)",
  },
});

const NavIcon = styled(IconButton)({
  color: Colors.LIGHT_GRAY_BFC2C6,
  "&:hover": {
    backgroundColor: "transparent",
    color: Colors.WHITE,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
});

// Experiment not having labels
/*
const NavLabel = styled(Typography)({
  fontSize: "11px",
  fontWeight: 500,
  color: Colors.LIGHT_GRAY_BFC2C6,
  textAlign: "center",
  lineHeight: 0.5,
});
*/

interface BottomNavigationProps {
  onClickMore: () => void;
}

export function BottomNavigation({ onClickMore }: BottomNavigationProps) {
  // const { t } = useTranslation("navigation");
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { lazoView } = useLazoView();
  const { amountUnread, amountChatUnread } = useNotifications();

  // Only render on mobile
  if (!isMobile) {
    return null;
  }

  const handleTasksClick = () => {
    if (lazoView === LazoView.LAZO_ONE) {
      navigate(LazoPaths.TASKS);
    } else {
      navigate(LazoPaths.TASKS_ADMIN);
    }
  };

  const handleInboxClick = () => {
    if (lazoView === LazoView.LAZO_ONE) {
      navigate(LazoPaths.ONE_COMMUNICATIONS);
    } else {
      navigate(LazoPaths.PORTAL_INBOX);
    }
  };

  const handleNotificationsClick = () => {
    if (lazoView === LazoView.LAZO_ONE) {
      navigate(LazoPaths.NOTIFICATIONS);
    } else {
      navigate(LazoPaths.PORTAL_NOTIFICATIONS);
    }
  };

  return (
    <>
      {/* Global styles are now handled in LazoAppContainer */}
      <BottomNavigationContainer>
        <NavButton onClick={onClickMore}>
          <NavIcon disableRipple>
            <MoreHorizIcon />
          </NavIcon>
          {/* <NavLabel>{t("footer.more")}</NavLabel>*/}
        </NavButton>

        <NavButton onClick={handleTasksClick}>
          <NavIcon disableRipple>
            <TaskIcon />
          </NavIcon>
          {/* <NavLabel>{t("drawer.lazoOne.tasks")}</NavLabel> */}
        </NavButton>

        <NavButton onClick={handleInboxClick}>
          <Box position="relative">
            <NavIcon disableRipple>
              <MailIcon />
            </NavIcon>
            {amountChatUnread > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "2px",
                  right: "2px",
                  bgcolor: Colors.LAZO_PURPLE,
                  color: Colors.WHITE,
                  borderRadius: "50%",
                  minWidth: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "bold",
                }}
              >
                {amountChatUnread > 99 ? "99+" : amountChatUnread}
              </Box>
            )}
          </Box>
          {/* <NavLabel>{t("drawer.lazoOne.inbox")}</NavLabel>*/}
        </NavButton>

        <NavButton onClick={handleNotificationsClick}>
          <Box position="relative">
            <NavIcon disableRipple>
              <NotificationsIcon />
            </NavIcon>
            {amountUnread > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "2px",
                  right: "2px",
                  bgcolor: Colors.LAZO_PURPLE,
                  color: Colors.WHITE,
                  borderRadius: "50%",
                  minWidth: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "bold",
                }}
              >
                {amountUnread > 99 ? "99+" : amountUnread}
              </Box>
            )}
          </Box>
          {/* <NavLabel>{t("drawer.lazoOne.notifications")}</NavLabel>*/}
        </NavButton>
      </BottomNavigationContainer>
    </>
  );
}
